import React, { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useNavigate } from "react-router-dom";
import banner from "./assets/img/logo_transparent.png";
import defaultpet from "./assets/img/defaultpet.png";
import { getCurrentCoordinates } from "./Utils";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://systalk-dev.azure-api.net/wagtag";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY ||
    (process.env.NODE_ENV === "development" ? "1x00000000000000000000AA" : "");

function WizardAlertEmergencyContacts({ keyRingId, fromApp }) {
    const navigate = useNavigate();
    const turnstileRef = useRef(null);
    const [step, setStep] = useState(1);
    const [displayName, setDisplayName] = useState("");
    const [imageUri, setImageUri] = useState("");
    const [isRegistered, setIsRegistered] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [description, setDescription] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [submittingAlert, setSubmittingAlert] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileErrorMessage, setTurnstileErrorMessage] = useState("");

    useEffect(() => {
        const fetchEmergencyContactProfile = async () => {
            try {
                const response = await fetch(
                    `${apiBaseUrl}/api/Account/PetOwnerPublicProfile?keyRingId=${keyRingId}`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch Pet Owner Public Profile.");
                }

                const profile = await response.json();
                if (!profile) {
                    throw new Error("Pet Owner Public Profile was not found.");
                }

                setDisplayName(profile.DisplayName || "");
                setImageUri(profile.ImageUri || "");
                setIsRegistered(true);
            } catch (error) {
                console.error("Error fetching emergency contact profile:", error);
                setIsRegistered(false);
                setStep(1);
            }
        };

        fetchEmergencyContactProfile();
    }, [keyRingId]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!phoneNumber.trim()) {
            setErrorMessage("Please provide a contact phone number.");
            return;
        }

        if (!turnstileToken) {
            setTurnstileErrorMessage("Please complete the security check before alerting the emergency contacts.");
            return;
        }

        setSubmittingAlert(true);
        setErrorMessage("");
        setTurnstileErrorMessage("");

        const { latitude, longitude } = await getCurrentCoordinates();

        // Prepend phone number to description
        const combinedDescription = `An emergency has occured and I'm unable to care for my pets.  For further information call ${phoneNumber.trim()}. ${(description || "").trim()}`;

        const apiUrl = `${apiBaseUrl}/api/Post/EmergencyKeyringPost`;

        try {
            const payload = {
                keyRingId: keyRingId,
                Description: combinedDescription,
                TurnstileToken: turnstileToken,
            };

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const responseBody = await response.json();
                navigate("/thank-you", { state: { latitude: latitude, longitude: longitude, responseBody: responseBody, fromApp: fromApp, isEmergencyAlert: true } });
            } else {
                const errorData = await response.json().catch(() => null);
                const apiErrorMessage = typeof errorData === "string"
                    ? errorData
                    : errorData?.Message || errorData?.message || "Failed to alert the pet owner's emergency contacts.";

                setTurnstileToken("");
                turnstileRef.current?.reset();
                setErrorMessage(apiErrorMessage);
            }
        } catch (error) {
            console.error("Error alerting emergency contacts:", error);
            setErrorMessage("We couldn't alert the emergency contacts. Please try again.");
            setTurnstileToken("");
            turnstileRef.current?.reset();
        } finally {
            setSubmittingAlert(false);
        }
    };

    const renderWelcomeStep = () => {
        if (isRegistered === false) {
            return (
                <div style={styles.formRow}>
                    <h3>This emergency key ring hasn't been activated</h3>
                    <p style={{ ...styles.infoText, marginTop: "10px" }}>
                        For more information see the instructions card that came with your WagTag order, or follow the setup instructions {" "}
                        <a href="https://www.wagtag.com.au/how-to-setup-your-keyring" target="_blank" rel="noopener noreferrer">here</a>.
                    </p>
                    <p style={styles.infoText}>
                        If you're trying to alert someone's emergency contacts, please double-check the Tag ID on the key ring and try again.
                    </p>
                </div>
            );
        }

        return (
            <div style={styles.formRow}>
                <div style={styles.emergencyProfile}>
                    <div style={styles.emergencyImageContainer}>
                        <img
                            src={imageUri || defaultpet}
                            alt={displayName || "Emergency key-ring owner"}
                            style={styles.emergencyImage}
                        />
                    </div>
                    <div style={styles.emergencyDisplayName}>
                        {displayName || "Emergency Contact Profile"}
                    </div>
                </div>
                <p style={styles.infoText}>
                    Press next to notify this person's friends that a pet is home alone and needs care.
                </p>
            </div>
        );
    };

    const renderContactDetailsStep = () => (
        <div>
            <div style={styles.formRow}>
                <h3>Your Contact Details</h3>
                <input
                    id="contactPhone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    placeholder="Enter your contact phone number"
                    style={styles.textInput}
                />
                <textarea
                    id="description"
                    rows="6"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Friends will be notified that a pet is home alone and needs care. Enter any additional information."
                    style={styles.textArea}
                />
                {errorMessage && (
                    <div style={styles.alertError} role="alert">
                        <h3 style={styles.alertErrorTitle}>Notification not sent</h3>
                        <p style={styles.alertErrorMessage}>{errorMessage}</p>
                    </div>
                )}
                <div style={styles.turnstileContainer}>
                    {turnstileSiteKey ? (
                        <Turnstile
                            ref={turnstileRef}
                            siteKey={turnstileSiteKey}
                            onSuccess={(token) => {
                                setTurnstileToken(token);
                                setTurnstileErrorMessage("");
                            }}
                            onExpire={() => setTurnstileToken("")}
                            onError={() => {
                                setTurnstileToken("");
                                setTurnstileErrorMessage("The security check could not be completed. Please try again.");
                            }}
                            options={{
                                action: "emergency_keyring_post",
                                appearance: "interaction-only", // always show the challenge when the user interacts with the widget
                                size: "flexible",
                                theme: "light",
                            }}
                        />
                    ) : (
                        <p style={styles.errorMessage}>
                            The security check is not configured. Please contact WagTag support.
                        </p>
                    )}
                    {turnstileErrorMessage && (
                        <p style={styles.errorMessage}>{turnstileErrorMessage}</p>
                    )}
                </div>
            </div>
            <div style={styles.navigationContainer}>
                <hr style={styles.hr} />
                <div style={styles.navigationRow}>
                    <button style={styles.navigationButton} onClick={() => setStep(1)}>
                        Back
                    </button>
                    <button
                        style={{
                            ...styles.navigationButton,
                            opacity: phoneNumber.trim() && turnstileToken ? 1 : 0.6,
                            cursor: phoneNumber.trim() && turnstileToken ? "pointer" : "not-allowed",
                        }}
                        onClick={handleSubmit}
                        disabled={!phoneNumber.trim() || !turnstileToken || submittingAlert}
                    >
                        Notify Contacts
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={styles.page}>
            <main style={styles.main}>
                <header style={styles.header}>
                    <div style={styles.headerRow}>
                        <img src={banner} alt="WagTag" style={styles.headerImage} />
                        <div style={styles.headerTitle}>Notify Emergency Contacts</div>
                    </div>
                </header>
                <div style={styles.form}>
                    {step === 1 ? renderWelcomeStep() : renderContactDetailsStep()}
                    {step === 1 && isRegistered === true && (
                        <div style={styles.navigationContainer}>
                            <hr style={styles.hr} />
                            <div style={{ textAlign: "right" }}>
                                <button style={styles.navigationButton} onClick={() => setStep(2)}>
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                    <footer style={styles.footer}>
                        <p>
                            Visit us at <a href="https://www.wagtag.com.au" target="_blank" rel="noopener noreferrer">www.wagtag.com.au</a>
                        </p>
                    </footer>
                </div>
            </main>
        </div>
    );
}

const styles = {
    page: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#d3d3d3",
    },
    main: {
        flex: 1,
        padding: "10px",
        maxWidth: "600px",
        margin: "0 auto",
        width: "95%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    header: {
        backgroundColor: "#4CAF50",
        color: "white",
        padding: "6px 10px",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
    },
    headerImage: {
        width: "auto",
        height: "56px",
        maxHeight: "56px",
        objectFit: "contain",
        borderRadius: "6px",
    },
    headerTitle: {
        flex: 1,
        textAlign: "right",
        fontSize: "18px",
        fontWeight: 700,
        lineHeight: 1.1,
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        backgroundColor: "#ffffff",
        padding: "0 20px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        minHeight: "50vh",
    },
    formRow: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        marginBottom: "5px",
        marginRight: "20px",
        minHeight: "30vh",
    },
    emergencyProfile: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        margin: "12px 0",
    },
    emergencyImageContainer: {
        width: "140px",
        height: "140px",
        border: "2px solid #ccc",
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: "#f9f9f9",
    },
    emergencyImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    emergencyDisplayName: {
        color: "#222",
        fontSize: "22px",
        fontWeight: 700,
        textAlign: "center",
    },
    infoText: {
        fontSize: "18px",
        color: "#333",
        lineHeight: 1.4,
        marginTop: "10px",
    },
    textInput: {
        width: "100%",
        padding: "10px",
        fontSize: "18px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        boxSizing: "border-box",
    },
    textArea: {
        width: "100%",
        padding: "10px",
        fontSize: "18px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "10px",
        boxSizing: "border-box",
    },
    turnstileContainer: {
        width: "100%",
        minHeight: "65px",
        marginTop: "16px",
    },
    alertError: {
        marginTop: "16px",
        padding: "14px 16px",
        backgroundColor: "#fff3f2",
        border: "2px solid #c62828",
        borderRadius: "4px",
    },
    alertErrorTitle: {
        color: "#9f1c1c",
        fontSize: "18px",
        margin: "0 0 6px",
    },
    alertErrorMessage: {
        color: "#5f1717",
        fontSize: "16px",
        lineHeight: 1.4,
        margin: 0,
    },
    navigationContainer: {
        display: "flex",
        flexDirection: "column",
        marginTop: "auto",
    },
    navigationRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: "10px",
    },
    navigationButton: {
        padding: "16px 32px",
        minWidth: "120px",
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
    },
    hr: {
        border: "none",
        borderTop: "1px solid #ccc",
        margin: "20px 0",
        width: "100%",
    },
    footer: {
        backgroundColor: "#ffffff",
        paddingTop: "20px",
        textAlign: "center",
    },
    errorMessage: {
        color: "red",
        marginTop: "10px",
    },
};

export default WizardAlertEmergencyContacts;
